import { IMappingConnectorOwnProps } from './MappingConnector';
import { ContextProviderNode } from '@decisions/server/DecisionsFramework/Design/Properties';
import { InputMappingConstants, OutputMappingConstants } from 'components/property-editors/MappingConstants';
import { getAllValidationIssues } from 'utils/ValidationUtils';
import { LEFT_COLUMN_EDGE } from './MappingConnectors';
import {
  DATA_PATH_SEPARATOR,
  MAPPING_CATEGORY_PREFIX,
  mappingTypeRendersInlineEditor,
  StepDataMappingNode,
  getMappingElementId,
  getDataElementId,
} from '../utils/MappingUtils';
import { PROVIDER_NODE_PATH_SEPARATOR } from 'components/property-editors/utils/ProviderNodeUtils';
import {
  IDragNewMappingState,
  MappingPanelPosition,
  MappingPanelType,
  IMappingRowDimensions,
} from '../MappingEditorStore';

export const MAPPING_ROW_HEIGHT = 42; //px - should be in sync with .less var

export function getMappings(
  stepNodes: StepDataMappingNode[],
  visibleFlowDataNodes: string[],
  filteredFlowDataNodes: string[],
  visibleStepNodes: string[],
  leftToRight = true,
  bothFiltersUnmapped = false
) {
  if (bothFiltersUnmapped) {
    return { mappings: [], validationIssueForDataDesc: new Map() };
  }

  const props: IMappingConnectorOwnProps[] = [];
  const validationIssueForDataDesc = new Map();

  stepNodes.forEach(node => {
    const mappingName = node.mappingName;
    const isOverlayType = mappingTypeRendersInlineEditor(mappingName);
    if (
      mappingName !== InputMappingConstants.MAPPING_SELECT &&
      mappingName !== OutputMappingConstants.MAPPING_CHANGE &&
      !isOverlayType
    ) {
      return;
    }

    const nodeName = node.nodeName;
    const dataPath = node.dataPath;

    const isInvalid = node.validation.fatal > 0;
    const isFlowDataPathFiltered = filteredFlowDataNodes.includes(dataPath);

    const dataIndex = isFlowDataPathFiltered ? -1 : getDataDescIndex(visibleFlowDataNodes, dataPath);
    const dataElementPath = dataPath; // for now at least, until we need it to be something else.
    const disconnected = isFlowDataPathFiltered ? false : dataIndex === -1;
    const { stepIndex, stepPath } = getStepNodeIndexAndPath(visibleStepNodes, nodeName, isOverlayType);
    const rightHidden = leftToRight ? node.filtered : dataIndex === -1;
    const leftHidden = leftToRight ? dataIndex === -1 : node.filtered;
    const flowIndex = isOverlayType ? -1 : dataIndex;
    const startIndex = leftToRight ? flowIndex : stepIndex;
    const endIndex = leftToRight ? stepIndex : flowIndex;
    const startId = leftToRight ? getDataElementId(dataElementPath) : getMappingElementId(stepPath);
    const endId = leftToRight ? getMappingElementId(stepPath) : getDataElementId(dataElementPath);

    if (!(isOverlayType || disconnected) && isInvalid) {
      // TODO I bet we could move this datadesc check to MappingUtils.getStepNodeData, or getVisibleFlowDataNodes if downstream of getStepNodeData
      validationIssueForDataDesc.set(dataPath, node.validation.issues);
    }
    props.push({
      startIndex,
      endIndex,
      dataPath,
      nodeName,
      isOneSided: isOverlayType || disconnected,
      isInvalid,
      rightHidden,
      leftHidden,
      startId,
      endId,
    });
  });
  return {
    mappings: props,
    validationIssuesForDesc: validationIssueForDataDesc,
  };
}

export function getConnectorY(rowIndex: number, scroll = 0, elementId = '', rowHeights?: IMappingRowDimensions) {
  const row = rowHeights?.get(elementId);
  if (rowHeights && row) {
    return row.top + row.height / 2 - scroll;
    // return rowElement.offsetTop + (rowHeights.get(elementId) || MAPPING_ROW_HEIGHT) / 2 - scroll;
  }
  const rowElement = elementId && document.getElementById(elementId);
  return rowElement
    ? rowElement.offsetTop + rowElement.offsetHeight / 2 - scroll // read the actual element top:
    : rowIndex * MAPPING_ROW_HEIGHT + MAPPING_ROW_HEIGHT / 2 - scroll; // index * standard height
}

export function nodeIsInvalid(node: ContextProviderNode) {
  return getAllValidationIssues(node).fatal > 0;
}

// exported because we should unit test this
export function getDataDescIndex(visibleNodes: string[], dataPath: string) {
  return getNearestVisiblePathIndex(visibleNodes, dataPath, DATA_PATH_SEPARATOR).index;
}

function getNearestVisiblePathIndex(visibleNodes: string[], path: string, separator: string) {
  // might need a away to handle which path was matched, and whether it was the requested
  // path or not, and if not, we can skip rendering overlay types
  let exactMatch = true;
  let index = visibleNodes.indexOf(path);
  while (index < 0 && path.includes(separator)) {
    exactMatch = false;
    path = path.substring(0, path.lastIndexOf(separator));
    index = visibleNodes.indexOf(path);
  }
  return { exactMatch, index, path };
}

export function getStepNodeIndexAndPath(nodes: string[], path: string, overlayType = false) {
  // since category-only nodes will have category prefix, they won't match, so we need to strip that out for this check
  // TODO better to track bare categories another way?
  nodes = nodes.map(node => node.replace(MAPPING_CATEGORY_PREFIX, ''));
  const { index, exactMatch, path: stepPath } = getNearestVisiblePathIndex(nodes, path, PROVIDER_NODE_PATH_SEPARATOR);
  const stepIndex = exactMatch || overlayType === false ? index : -1;
  return { stepIndex, stepPath };
}

export function getStepNodeIndex(nodes: string[], path: string, overlayType = false) {
  return getStepNodeIndexAndPath(nodes, path, overlayType).stepIndex;
}

interface IGetOppositeYOptions {
  mappings: IMappingConnectorOwnProps[];
  side: MappingPanelType;
  selected: string;
  leftToRight: boolean;
  rowHeights?: IMappingRowDimensions;
}
export function getMappedOppositeY({
  mappings,
  side,
  leftToRight,
  rowHeights,
  selected,
}: IGetOppositeYOptions): number {
  if (!selected) {
    return -1;
  }
  const nameKey: keyof IMappingConnectorOwnProps = side === MappingPanelType.FLOW ? 'nodeName' : 'dataPath';
  const mapping = mappings.find((mapping: IMappingConnectorOwnProps) => mapping[nameKey] === selected);

  if (!mapping) {
    return -1;
  }

  const { startIndex, endIndex, startId, endId } = mapping;

  const index = leftToRight
    ? side === MappingPanelType.FLOW
      ? startIndex
      : endIndex
    : side === MappingPanelType.FLOW
    ? endIndex
    : startIndex;

  const id = leftToRight
    ? side === MappingPanelType.FLOW
      ? startId
      : endId
    : side === MappingPanelType.FLOW
    ? endId
    : startId;

  const rowElement = id && rowHeights?.get(id);
  console.log(rowElement, rowHeights, id);
  const height = rowElement && index > 0 ? rowElement.top + rowElement.height : index * MAPPING_ROW_HEIGHT;

  return index > -1 ? height : index;
}

export interface IMappingConnectorDraggingProps {
  startSide: MappingPanelPosition;
  startY: number;
  leftToRight: boolean;
  targetRows: string[];
  targetSide: MappingPanelType;
  hitY?: number;
  hitSide?: MappingPanelPosition;
  targetScroll: number;
}

export interface IMappingConnectorHoverProps {
  startSide: MappingPanelPosition;
  startY: number;
}

export function getDraggingProps(
  visibleFlowDataNodes: string[],
  visibleStepNodes: string[],
  stepScroll: number,
  flowScroll: number,
  hoveredStepNodeId: string,
  hoveredFlowNodeId: string,
  draggingFrom?: IDragNewMappingState,
  leftToRight = true,
  rowHeights?: IMappingRowDimensions
): IMappingConnectorDraggingProps | undefined {
  if (!draggingFrom) {
    return;
  }

  const startPath = draggingFrom.path;
  let startIndex =
    draggingFrom.side === MappingPanelType.FLOW
      ? getDataDescIndex(visibleFlowDataNodes, startPath)
      : getStepNodeIndex(visibleStepNodes, startPath);

  if (startIndex === -1) {
    // did we have a reason we might fail to find an index from a path? there's even a Jest test for this...
    return;
  }

  let targetRows = draggingFrom.side === MappingPanelType.FLOW ? visibleStepNodes : visibleFlowDataNodes;

  leftToRight =
    (draggingFrom.side === MappingPanelType.FLOW && leftToRight) ||
    (draggingFrom.side === MappingPanelType.STEP && !leftToRight);

  let startY = 0;
  const startSide = leftToRight ? MappingPanelPosition.LEFT : MappingPanelPosition.RIGHT;

  let hitY: number | undefined;
  let hitSide: MappingPanelPosition | undefined;
  let targetSide: MappingPanelType;
  let targetScroll = 0;

  if (draggingFrom.side === MappingPanelType.FLOW) {
    // flow to step Y
    targetSide = MappingPanelType.STEP;
    targetScroll = stepScroll;
    const startIndex = getDataDescIndex(visibleFlowDataNodes, startPath);
    startY = getConnectorY(startIndex, flowScroll);
    const { stepIndex, stepPath } = getStepNodeIndexAndPath(visibleStepNodes, hoveredStepNodeId);
    hitY = hoveredStepNodeId
      ? getConnectorY(stepIndex, targetScroll, getMappingElementId(stepPath), rowHeights)
      : undefined;
  } else {
    // find flow node Y
    targetSide = MappingPanelType.FLOW;
    targetScroll = flowScroll;
    const { stepIndex, stepPath } = getStepNodeIndexAndPath(visibleStepNodes, startPath);
    startY = getConnectorY(stepIndex, stepScroll, getMappingElementId(stepPath));
    const targetIndex = getDataDescIndex(visibleFlowDataNodes, hoveredFlowNodeId);
    hitY = hoveredFlowNodeId ? getConnectorY(targetIndex, targetScroll) : undefined;
  }

  hitSide = hitY ? (leftToRight ? MappingPanelPosition.RIGHT : MappingPanelPosition.LEFT) : undefined;

  return { startSide, startY, hitY, hitSide, leftToRight, targetRows, targetSide, targetScroll };
}

export function getRowHoveredPropsForConnectors(
  stepNodes: string[],
  visibleFlowDataNodes: string[],
  flowPanelHoverNodeId: string,
  stepPanelHoverNodeId: string,
  flowPanelScrollPosition: number | undefined,
  stepPanelScrollPosition: number | undefined,
  mappings: IMappingConnectorOwnProps[],
  leftToRight = true
): IMappingConnectorHoverProps | undefined {
  let startY = 0;
  let startSide = 0;

  if (flowPanelHoverNodeId) {
    const indexKey: keyof IMappingConnectorOwnProps = leftToRight ? 'startIndex' : 'endIndex';
    const startIndexDataDesc = getDataDescIndex(visibleFlowDataNodes, flowPanelHoverNodeId);
    if (startIndexDataDesc === -1) return;

    if (mappings.find(data => data[indexKey] === startIndexDataDesc)) return; //already connected

    startY = getConnectorY(startIndexDataDesc, flowPanelScrollPosition);
    startSide = leftToRight ? MappingPanelPosition.LEFT : MappingPanelPosition.RIGHT;
    return { startSide: startSide, startY };
  } else if (stepPanelHoverNodeId) {
    const indexKey: keyof IMappingConnectorOwnProps = leftToRight ? 'endIndex' : 'startIndex';
    const { stepIndex, stepPath } = getStepNodeIndexAndPath(stepNodes, stepPanelHoverNodeId);
    if (stepIndex === -1) return;

    if (mappings.find(data => data[indexKey] === stepIndex)) return; //already connected

    startY = getConnectorY(stepIndex, stepPanelScrollPosition, getMappingElementId(stepPath));
    startSide = !leftToRight ? MappingPanelPosition.LEFT : MappingPanelPosition.RIGHT;

    return { startSide, startY };
  }
}

/**
 * Lookup a node path from x/y coordinates
 * // TODO adjust for variable row heights - can just get by querySelectorAll, and measure, probably, right?
 * @param x mouse position, relative to connector area
 * @param y mouse position, relative to connector area
 * @param visibleNodes list of visible flow or step node targets (depending on direction)
 * @param currentHit current hit target if any, so as not to emit any duplicate events
 * @param source source column user is dragging from
 * @param forInput true if it's input mapping, affects left to right, right to left of source
 * @returns path of target or empty string.
 */
export function getDropNodeFromXY(
  x: number,
  y: number,
  visibleNodes: string[],
  rowHeights: number[],
  leftToRight: boolean,
  rightEdge: number,
  scrollPosition = 0
) {
  // would normally prefer object arg pattern for this many args, but since it'll be on mouse-move, trying to hit
  // garbage collection as little as possible.

  y += scrollPosition;

  let i = -1;
  let h = 0;

  while (y > 0 && i < rowHeights.length && y > h) {
    i++;
    h += rowHeights[i];
  }

  if (y > 0 && y < h && ((leftToRight && x > rightEdge - 30) || (!leftToRight && x < LEFT_COLUMN_EDGE + 30))) {
    const name = visibleNodes[i];
    return name.startsWith(MAPPING_CATEGORY_PREFIX) ? '' : visibleNodes[i] || '';
  }

  return '';
}
